import ReactPixel from 'react-facebook-pixel';
import ReactGA from 'react-ga4';

// Analytics IDs from environment variables
const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID;
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID;
const HOTJAR_ID = import.meta.env.VITE_HOTJAR_ID;
const HOTJAR_SV = parseInt(import.meta.env.VITE_HOTJAR_SV || '6', 10); // Hotjar script version

// Track if services have been initialized
let isFBPixelInitialized = false;
let isGA4Initialized = false;
let isHotjarInitialized = false;

// ============================================
// Cross-Domain Cookie Consent
// ============================================

// Cookie domain for cross-subdomain sharing
// Use empty string for localhost to allow local development
const COOKIE_DOMAIN = window.location.hostname === 'localhost' ? '' : '.storyplannerpro.de';
const COOKIE_NAME = 'cookieConsent';
const COOKIE_MAX_AGE = 31536000; // 1 year in seconds

/**
 * Set cookie consent with cross-domain support
 * Cookie is shared between storyplannerpro.de and app.storyplannerpro.de
 */
export const setCookieConsent = (preferences) => {
  const value = encodeURIComponent(JSON.stringify(preferences));
  const domainAttr = COOKIE_DOMAIN ? `; domain=${COOKIE_DOMAIN}` : '';
  document.cookie = `${COOKIE_NAME}=${value}${domainAttr}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax; Secure`;
  // Also store in localStorage as fallback
  localStorage.setItem(COOKIE_NAME, JSON.stringify(preferences));
};

/**
 * Get cookie consent from cross-domain cookie
 * Falls back to localStorage if cookie not found
 */
export const getCookieConsent = () => {
  // Try to read from cookie first
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    const equalsIndex = trimmed.indexOf('=');
    if (equalsIndex === -1) continue;

    const name = trimmed.substring(0, equalsIndex);
    const value = trimmed.substring(equalsIndex + 1);

    if (name === COOKIE_NAME && value) {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch (e) {
        console.warn('[Analytics] Failed to parse cookie consent:', e);
      }
    }
  }

  // Fallback to localStorage
  const stored = localStorage.getItem(COOKIE_NAME);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.warn('[Analytics] Failed to parse localStorage consent:', e);
    }
  }

  return null;
};

// ============================================
// Advanced Matching - Email Hashing
// ============================================

/**
 * Hash a string using SHA256 for Facebook Advanced Matching.
 * Following Meta's requirements: trimmed, then SHA256 hashed.
 *
 * @param {string} value - Value to hash
 * @param {boolean} lowercase - Whether to lowercase before hashing (true for email)
 * @returns {Promise<string|null>} SHA256 hashed value or null
 */
const hashSHA256 = async (value, lowercase = false) => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  try {
    // Normalize: trim (and optionally lowercase)
    let normalized = value.trim();
    if (lowercase) {
      normalized = normalized.toLowerCase();
    }

    // Use Web Crypto API for SHA256
    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  } catch (error) {
    console.warn('[Analytics] Failed to hash value:', error);
    return null;
  }
};

/**
 * Hash email using SHA256 for Facebook Advanced Matching.
 * Following Meta's requirements: lowercase, trimmed, then SHA256 hashed.
 *
 * @param {string} email - User's email address
 * @returns {Promise<string|null>} SHA256 hashed email or null
 */
export const hashEmail = async (email) => {
  return hashSHA256(email, true); // lowercase for email
};

/**
 * Hash external ID (user ID) using SHA256 for Facebook Advanced Matching.
 * Following Meta's recommendation for external_id hashing.
 *
 * @param {string} externalId - User's internal ID
 * @returns {Promise<string|null>} SHA256 hashed external ID or null
 */
export const hashExternalId = async (externalId) => {
  return hashSHA256(externalId, false); // no lowercase for IDs
};

/**
 * Track Facebook Pixel event with Advanced Matching (hashed user data).
 * Uses raw fbq() for full control over user_data parameter.
 *
 * @param {string} eventName - Event name (Purchase, CompleteRegistration, etc.)
 * @param {Object} eventData - Event parameters
 * @param {Object} options - Additional options
 * @param {string} options.eventId - Event ID for CAPI deduplication
 * @param {string} options.hashedEmail - Pre-hashed email (SHA256)
 * @param {string} options.hashedExternalId - Pre-hashed user ID (SHA256)
 */
const trackPixelWithAdvancedMatching = (eventName, eventData, options = {}) => {
  const { eventId, hashedEmail, hashedExternalId } = options;

  if (!isFBPixelInitialized || !FB_PIXEL_ID) {
    return;
  }

  // Build extended options for Advanced Matching
  const extendedOptions = {};

  if (eventId) {
    extendedOptions.eventID = eventId;
  }

  // Build user_data for Advanced Matching
  const userData = {};
  if (hashedEmail) {
    userData.em = hashedEmail; // 'em' is the key for hashed email
  }
  if (hashedExternalId) {
    userData.external_id = hashedExternalId; // hashed user ID
  }

  if (Object.keys(userData).length > 0) {
    extendedOptions.user_data = userData;
  }

  // Use raw fbq for Advanced Matching support
  if (typeof window.fbq === 'function') {
    if (Object.keys(extendedOptions).length > 0) {
      window.fbq('trackSingle', FB_PIXEL_ID, eventName, eventData, extendedOptions);
    } else {
      window.fbq('trackSingle', FB_PIXEL_ID, eventName, eventData);
    }
  } else {
    // Fallback to ReactPixel if fbq not available
    ReactPixel.track(eventName, eventData);
  }
};

// No need for hasConsent anymore - we simply check if analytics are initialized
// If they're initialized, user has consented. If not, they haven't.

/**
 * Initialize Facebook Pixel
 * Call this once when the app starts
 */
export const initFacebookPixel = () => {
  if (!FB_PIXEL_ID) {
    console.warn('[Analytics] Facebook Pixel ID not configured');
    return;
  }

  if (isFBPixelInitialized) {
    console.warn('[Analytics] Facebook Pixel already initialized');
    return;
  }

  try {
    ReactPixel.init(FB_PIXEL_ID, {
      autoConfig: true,
      debug: import.meta.env.DEV, // Enable debug in development
    });

    // GDPR Compliance: Revoke consent by default
    // Grant consent after user accepts cookies/tracking
    ReactPixel.revokeConsent();

    isFBPixelInitialized = true;
    console.log('[Analytics] Facebook Pixel initialized');
  } catch (error) {
    console.error('[Analytics] Failed to initialize Facebook Pixel:', error);
  }
};

/**
 * No consent mode setup needed - we simply don't load any scripts until consent
 * @deprecated Not needed anymore - we just don't load scripts without consent
 */
export const setConsentDefaults = () => {
  // No-op - we don't load any scripts until user gives consent
  console.log('[Analytics] No consent mode needed - scripts not loaded yet');
};

/**
 * Manually load the gtag.js script (only after consent)
 * This prevents automatic loading by react-ga4
 */
const loadGtagScript = () => {
  return new Promise((resolve, reject) => {
    // Check if gtag script is already loaded
    if (window.gtag && typeof window.gtag === 'function') {
      console.log('[Analytics] gtag.js already loaded');
      resolve();
      return;
    }

    // Create script element for gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;

    script.onload = () => {
      console.log('[Analytics] gtag.js script loaded');

      // Initialize gtag if not already done in index.html
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
      }

      // Set initialization timestamp
      window.gtag('js', new Date());

      resolve();
    };

    script.onerror = () => {
      console.error('[Analytics] Failed to load gtag.js script');
      reject(new Error('Failed to load gtag.js'));
    };

    // Append to head
    document.head.appendChild(script);
    console.log('[Analytics] Loading gtag.js script...');
  });
};

/**
 * Initialize Google Analytics 4
 * IMPORTANT: Should only be called AFTER user consent is granted
 * This ensures no tracking happens before consent
 */
export const initGA4 = async () => {
  if (!GA4_MEASUREMENT_ID) {
    console.warn('[Analytics] GA4 Measurement ID not configured');
    return;
  }

  if (isGA4Initialized) {
    console.warn('[Analytics] GA4 already initialized');
    return;
  }

  try {
    // First, manually load the gtag.js script (after consent)
    await loadGtagScript();

    // Configure GA4 with gtag directly (no react-ga4 auto-loading)
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA4_MEASUREMENT_ID, {
        'send_page_view': false, // We track manually
        'allow_google_signals': true, // Enabled after consent
        'allow_ad_personalization_signals': true, // Enabled after consent
        'anonymize_ip': true, // GDPR compliance
        // Enable automatic events (user already gave consent)
        'page_view': false, // We track manually
        'scroll': true,
        'outbound_clicks': true,
        'site_search': true,
        'video_engagement': true,
        'file_downloads': true,
      });

      // Now initialize ReactGA to use the already-loaded gtag
      ReactGA.initialize(GA4_MEASUREMENT_ID, {
        gaOptions: {
          anonymize_ip: true,
        },
        gtagOptions: {
          send_page_view: false,
        },
      });

      isGA4Initialized = true;
      console.log('[Analytics] GA4 initialized after consent (Enhanced Measurement enabled)');
    } else {
      throw new Error('gtag function not available after script load');
    }
  } catch (error) {
    console.error('[Analytics] Failed to initialize GA4:', error);
  }
};

/**
 * Initialize Google Ads (gtag.js)
 * Call this once when the app starts
 */
export const initGoogleAds = () => {
  if (!GOOGLE_ADS_ID) {
    console.warn('[Analytics] Google Ads ID not configured');
    return;
  }

  try {
    // Google Ads uses the same gtag as GA4
    // Just need to configure the conversion ID
    if (typeof window.gtag === 'function') {
      window.gtag('config', GOOGLE_ADS_ID);
      console.log('[Analytics] Google Ads initialized');
    } else {
      console.warn('[Analytics] gtag not available for Google Ads');
    }
  } catch (error) {
    console.error('[Analytics] Failed to initialize Google Ads:', error);
  }
};

/**
 * Initialize Hotjar
 * IMPORTANT: Should only be called AFTER user consent is granted
 * Hotjar is used for session recordings and heatmaps
 */
export const initHotjar = () => {
  if (!HOTJAR_ID) {
    console.warn('[Analytics] Hotjar ID not configured');
    return;
  }

  if (isHotjarInitialized) {
    console.warn('[Analytics] Hotjar already initialized');
    return;
  }

  try {
    // Hotjar Tracking Code
    (function(h, o, t, j, a, r) {
      h.hj = h.hj || function() { (h.hj.q = h.hj.q || []).push(arguments) };
      h._hjSettings = { hjid: parseInt(HOTJAR_ID), hjsv: HOTJAR_SV };
      a = o.getElementsByTagName('head')[0];
      r = o.createElement('script');
      r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');

    isHotjarInitialized = true;
    console.log('[Analytics] Hotjar initialized');
  } catch (error) {
    console.error('[Analytics] Failed to initialize Hotjar:', error);
  }
};

/**
 * Disable Hotjar tracking
 * Call this when user revokes consent
 */
export const disableHotjar = () => {
  if (!isHotjarInitialized) return;

  try {
    // Hotjar provides a way to opt-out
    if (window.hj) {
      window.hj('optOut');
    }
  } catch (error) {
    // Silently fail - non-critical
  }
};

/**
 * Identify user for GA4 cross-device tracking.
 * Links sessions from the same user across devices/browsers.
 * Requires GA4 User-ID feature enabled in Admin → Data Settings.
 *
 * @param {string} userId - Internal user ID (Supabase UUID)
 */
export const identifyGA4User = (userId) => {
  if (!isGA4Initialized) return;

  try {
    // Set user_id via gtag
    if (typeof window.gtag === 'function') {
      window.gtag('set', 'user_id', userId);
    }

    // Also set via ReactGA for consistency
    ReactGA.set({ user_id: userId });
  } catch (error) {
    // Silently fail - non-critical
  }
};

/**
 * Identify user for Hotjar session linking.
 * Links multiple sessions from the same user across devices/browsers.
 * Requires Hotjar Business or Scale plan.
 *
 * @param {string} userId - Internal user ID (Supabase UUID)
 * @param {Object} attributes - Optional user attributes
 * @param {string} attributes.plan - Subscription plan (free, starter, pro)
 * @param {number} attributes.accounts_count - Number of connected Instagram accounts
 * @param {string} attributes.registered_at - ISO date of registration
 */
export const identifyHotjarUser = (userId, attributes = {}) => {
  if (!isHotjarInitialized || typeof window.hj !== 'function') return;

  try {
    // Filter out null/undefined values
    const cleanAttributes = Object.fromEntries(
      Object.entries(attributes).filter(([, v]) => v != null)
    );

    window.hj('identify', userId, cleanAttributes);
  } catch (error) {
    // Silently fail - non-critical
  }
};

/**
 * Track Hotjar event for filtering recordings.
 * Events allow filtering recordings by user actions.
 *
 * @param {string} eventName - Event name (e.g., 'checkout_started', 'quota_exceeded')
 */
export const trackHotjarEvent = (eventName) => {
  if (!isHotjarInitialized || typeof window.hj !== 'function') return;

  try {
    window.hj('event', eventName);
  } catch (error) {
    // Silently fail - non-critical
  }
};

/**
 * Grant user consent for tracking (GDPR)
 * Call this after user accepts cookies/tracking
 * @param {Object} options - Consent options
 * @param {boolean} options.analytics - Allow analytics cookies
 * @param {boolean} options.marketing - Allow marketing cookies
 */
export const grantConsent = (options = { analytics: true, marketing: true }) => {
  const { analytics = true, marketing = true } = options;

  console.log('[Analytics] Consent granted:', { analytics, marketing });

  // Update Google Consent Mode V2
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'ad_storage': marketing ? 'granted' : 'denied',
      'ad_user_data': marketing ? 'granted' : 'denied',
      'ad_personalization': marketing ? 'granted' : 'denied',
      'analytics_storage': analytics ? 'granted' : 'denied',
      'personalization_storage': analytics ? 'granted' : 'denied',
      'functionality_storage': 'granted',
      'security_storage': 'granted',
    });
    console.log('[Consent Mode V2] Updated to:', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied'
    });
  }

  // Facebook Pixel - grant consent if initialized
  if (isFBPixelInitialized && (analytics || marketing)) {
    ReactPixel.grantConsent();
    console.log('[Analytics] Facebook Pixel consent granted');
  }
};

/**
 * Revoke user consent for tracking (GDPR)
 * Call this if user rejects or revokes cookies/tracking
 */
export const revokeConsent = () => {
  console.log('[Analytics] Consent revoked');

  // Update Google Consent Mode V2 back to denied
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied',
      'personalization_storage': 'denied',
      'functionality_storage': 'granted',
      'security_storage': 'granted',
    });
    console.log('[Consent Mode V2] Revoked - all set to denied');
  }

  // If scripts were already loaded (user changed mind), revoke pixel consent
  if (isFBPixelInitialized) {
    ReactPixel.revokeConsent();
    console.log('[Analytics] Facebook Pixel consent revoked');
  }

  // Disable Hotjar if initialized
  disableHotjar();

  // Note: Scripts are already loaded, so some tracking may continue this session
  if (isGA4Initialized || isHotjarInitialized) {
    console.log('[Analytics] Note: Scripts already loaded - page refresh recommended');
  }
};

/**
 * Track page view
 * Call this on route changes
 */
export const trackPageView = () => {
  // Simple: If GA4 is not initialized, user hasn't consented
  if (!isGA4Initialized) {
    console.log('[Analytics] Page view not tracked - GA4 not initialized (no consent)');
    return;
  }

  // Facebook Pixel
  if (isFBPixelInitialized) {
    ReactPixel.pageView();
  }

  // Google Analytics 4
  if (isGA4Initialized) {
    ReactGA.send('pageview');
  }

  console.log('[Analytics] Page view tracked');
};

/**
 * Track custom event
 * @param {string} event - Event name
 * @param {object} data - Event data
 */
export const trackEvent = (event, data = {}) => {
  // Simple: If GA4 is not initialized, user hasn't consented
  if (!isGA4Initialized && !isFBPixelInitialized) {
    console.log('[Analytics] Event not tracked - no analytics initialized (no consent):', event);
    return;
  }

  // Facebook Pixel
  if (isFBPixelInitialized) {
    ReactPixel.track(event, data);
  }

  // Google Analytics 4
  if (isGA4Initialized) {
    ReactGA.event(event, data);
  }

  console.log('[Analytics] Event tracked:', event, data);
};

// ============================================
// App-specific tracking functions
// ============================================

/**
 * Track user registration
 * @param {Object} options - Registration options
 * @param {string} options.eventId - Event ID for CAPI deduplication
 * @param {string} options.hashedEmail - Pre-hashed email (SHA256) for Advanced Matching
 * @param {string} options.hashedExternalId - Pre-hashed user ID (SHA256) for Advanced Matching
 */
export const trackRegistration = (options = {}) => {
  const { eventId, hashedEmail, hashedExternalId } = options;

  // Simple: If not initialized, user hasn't consented
  if (!isGA4Initialized && !isFBPixelInitialized) {
    console.log('[Analytics] Registration not tracked - no consent');
    return;
  }

  const eventData = {
    content_name: 'User Registration',
    status: 'success',
  };

  // Facebook Pixel with Advanced Matching
  if (isFBPixelInitialized) {
    trackPixelWithAdvancedMatching('CompleteRegistration', eventData, {
      eventId,
      hashedEmail,
      hashedExternalId,
    });
  }

  // Google Analytics 4
  if (isGA4Initialized) {
    ReactGA.event('sign_up', {
      method: 'email',
    });
  }

  // Hotjar Event
  trackHotjarEvent('registration_complete');
};

/**
 * Track user login
 */
export const trackLogin = () => {
  trackEvent('Login', {
    content_name: 'User Login',
  });
};

/**
 * Track story upload
 * Sends engagement value to Meta for Power User optimization.
 * Meta can use this to find users who upload many stories (high engagement).
 * @param {number} count - Number of stories uploaded
 * @param {string} type - 'image' or 'video'
 */
const ENGAGEMENT_VALUE_PER_STORY = 5; // Estimated engagement value per story upload

export const trackStoryUpload = (count = 1, type = 'image') => {
  trackEvent('StoryUpload', {
    content_type: `story_${type}`,
    content_name: 'Story Upload',
    value: count * ENGAGEMENT_VALUE_PER_STORY,
    currency: 'EUR',
  });

  // Hotjar Event
  trackHotjarEvent('story_uploaded');
};

/**
 * Track story scheduled
 * @param {number} count - Number of stories scheduled
 */
export const trackStoryScheduled = (count = 1) => {
  trackEvent('Schedule', {
    content_type: 'story',
    content_name: 'Story Scheduled',
    value: count,
  });
};

/**
 * Track story approved
 */
export const trackStoryApproved = () => {
  trackEvent('CustomizeProduct', {
    content_type: 'story',
    content_name: 'Story Approved',
  });
};

/**
 * Track caption regeneration
 */
export const trackCaptionRegeneration = () => {
  trackEvent('CustomizeProduct', {
    content_type: 'caption',
    content_name: 'Caption Regeneration',
  });

  // Hotjar Event
  trackHotjarEvent('caption_regenerated');
};

/**
 * Track subscription view
 * @param {string} plan - Plan name (free, starter, pro)
 */
export const trackViewSubscription = (plan = null) => {
  trackEvent('ViewContent', {
    content_type: 'subscription',
    content_name: plan ? `View ${plan} Plan` : 'View Subscription Plans',
    content_category: 'subscription',
  });
};

/**
 * Track subscription initiation (clicked on a plan)
 * @param {string} plan - Plan name (starter, pro)
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @param {number} price - Plan price
 * @param {Object} options - Additional options
 * @param {string} options.eventId - Event ID for CAPI deduplication
 * @param {string} options.hashedEmail - Pre-hashed email (SHA256) for Advanced Matching
 * @param {string} options.hashedExternalId - Pre-hashed user ID (SHA256) for Advanced Matching
 */
export const trackInitiateCheckout = (plan, billingPeriod, price, options = {}) => {
  const { eventId, hashedEmail, hashedExternalId } = options;

  // Simple: If not initialized, user hasn't consented
  if (!isGA4Initialized && !isFBPixelInitialized) {
    console.log('[Analytics] Checkout initiation not tracked - no consent');
    return;
  }

  const fbEventData = {
    content_name: `${plan} Plan`,
    content_category: 'subscription',
    value: price,
    currency: 'EUR',
    predicted_ltv: price * 12,
    content_type: billingPeriod,
  };

  // Facebook Pixel with Advanced Matching
  if (isFBPixelInitialized) {
    trackPixelWithAdvancedMatching('InitiateCheckout', fbEventData, {
      eventId,
      hashedEmail,
      hashedExternalId,
    });
  }

  // GA4 Enhanced E-Commerce format
  if (isGA4Initialized) {
    ReactGA.event('begin_checkout', {
      currency: 'EUR',
      value: price,
      items: [{
        item_id: `${plan}_${billingPeriod}`,
        item_name: `${plan} Plan`,
        item_category: 'subscription',
        item_variant: billingPeriod,
        price: price,
        quantity: 1,
      }],
    });
  }

  // Hotjar Event
  trackHotjarEvent('checkout_started');
};

/**
 * Track successful subscription purchase
 * @param {string} plan - Plan name (starter, pro)
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @param {number} price - Plan price
 * @param {Object} options - Additional purchase data
 * @param {string} options.transactionId - Stripe transaction/session ID
 * @param {string} options.eventId - Event ID for CAPI deduplication
 * @param {string} options.hashedEmail - Pre-hashed email (SHA256) for Advanced Matching
 * @param {string} options.hashedExternalId - Pre-hashed user ID (SHA256) for Advanced Matching
 * @param {Array} options.addons - Array of addon objects {id, name, price}
 * @param {number} options.totalValue - Total purchase value including addons
 */
export const trackSubscriptionPurchase = (plan, billingPeriod, price, options = {}) => {
  const { transactionId, eventId, hashedEmail, hashedExternalId, addons = [], totalValue } = options;

  // Simple: If not initialized, user hasn't consented
  if (!isGA4Initialized && !isFBPixelInitialized) {
    console.log('[Analytics] Purchase not tracked - no consent');
    return;
  }

  // Calculate total value if not provided
  const finalValue = totalValue || (price + addons.reduce((sum, addon) => sum + (addon.price || 0), 0));

  // Facebook Pixel - Create contents array
  const fbContents = [
    {
      id: `${plan}_${billingPeriod}`,
      quantity: 1,
      item_price: price,
    }
  ];

  addons.forEach(addon => {
    fbContents.push({
      id: addon.id,
      quantity: 1,
      item_price: addon.price || 0,
    });
  });

  const fbEventData = {
    content_name: `${plan} Plan`,
    content_category: 'subscription',
    content_type: billingPeriod,
    value: finalValue,
    currency: 'EUR',
    predicted_ltv: finalValue * 12,
    num_items: fbContents.length,
    contents: fbContents,
  };

  if (transactionId) {
    fbEventData.transaction_id = transactionId;
  }

  if (addons.length > 0) {
    fbEventData.content_name += ` + ${addons.length} Add-on${addons.length > 1 ? 's' : ''}`;
    fbEventData.addon_ids = addons.map(a => a.id).join(',');
  }

  // Facebook Pixel with Advanced Matching
  if (isFBPixelInitialized) {
    trackPixelWithAdvancedMatching('Purchase', fbEventData, {
      eventId,
      hashedEmail,
      hashedExternalId,
    });
  }

  // GA4 Enhanced E-Commerce - Purchase Event
  if (isGA4Initialized) {
    const ga4Items = [{
      item_id: `${plan}_${billingPeriod}`,
      item_name: `${plan} Plan`,
      item_category: 'subscription',
      item_variant: billingPeriod,
      price: price,
      quantity: 1,
    }];

    // Add addons as separate items
    addons.forEach(addon => {
      ga4Items.push({
        item_id: addon.id,
        item_name: addon.name || addon.id,
        item_category: 'addon',
        price: addon.price || 0,
        quantity: 1,
      });
    });

    ReactGA.event('purchase', {
      transaction_id: transactionId || `sub_${Date.now()}`,
      currency: 'EUR',
      value: finalValue,
      items: ga4Items,
    });
  }

  // Google Ads Conversion Tracking
  if (GOOGLE_ADS_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/subscription_purchase`,
      value: finalValue,
      currency: 'EUR',
      transaction_id: transactionId,
    });
  }

  // Hotjar Event
  trackHotjarEvent('subscription_purchased');
};

/**
 * Track subscription start (Subscribe event)
 * Triggered when user subscribes to a paid plan (in addition to Purchase)
 * @param {string} plan - Plan name (starter, pro)
 * @param {string} billingPeriod - 'monthly' or 'yearly'
 * @param {number} price - Plan price
 * @param {Object} options - Additional options
 * @param {string} options.eventId - Event ID for CAPI deduplication
 * @param {string} options.hashedEmail - Pre-hashed email (SHA256) for Advanced Matching
 * @param {string} options.hashedExternalId - Pre-hashed user ID (SHA256) for Advanced Matching
 */
export const trackSubscribe = (plan, billingPeriod, price, options = {}) => {
  const { eventId, hashedEmail, hashedExternalId } = options;

  if (!isGA4Initialized && !isFBPixelInitialized) {
    console.log('[Analytics] Subscribe not tracked - no consent');
    return;
  }

  const predictedLtv = billingPeriod === 'yearly' ? price : price * 12;

  const fbEventData = {
    value: price,
    currency: 'EUR',
    predicted_ltv: predictedLtv,
    content_name: `${plan} Plan`,
    content_category: 'subscription',
  };

  // Facebook Pixel with Advanced Matching
  if (isFBPixelInitialized) {
    trackPixelWithAdvancedMatching('Subscribe', fbEventData, {
      eventId,
      hashedEmail,
      hashedExternalId,
    });
  }

  if (isGA4Initialized) {
    ReactGA.event('subscribe', {
      value: price,
      currency: 'EUR',
      items: [{
        item_id: `${plan}_${billingPeriod}`,
        item_name: `${plan} Plan`,
      }],
    });
  }

  // Hotjar Event
  trackHotjarEvent('subscribed');
};

/**
 * Track free trial start (StartTrial event)
 * Triggered when user starts with Free plan
 * @param {Object} options - Trial options
 * @param {string} options.eventId - Event ID for CAPI deduplication
 * @param {string} options.hashedEmail - Pre-hashed email (SHA256) for Advanced Matching
 * @param {string} options.hashedExternalId - Pre-hashed user ID (SHA256) for Advanced Matching
 * @param {number} options.predictedLtv - Predicted lifetime value if they convert (default: 288)
 */
export const trackStartTrial = (options = {}) => {
  const { eventId, hashedEmail, hashedExternalId, predictedLtv = 288 } = options;

  if (!isGA4Initialized && !isFBPixelInitialized) {
    console.log('[Analytics] StartTrial not tracked - no consent');
    return;
  }

  const fbEventData = {
    value: 0,
    currency: 'EUR',
    predicted_ltv: predictedLtv,
    content_name: 'Free Trial',
  };

  // Facebook Pixel with Advanced Matching
  if (isFBPixelInitialized) {
    trackPixelWithAdvancedMatching('StartTrial', fbEventData, {
      eventId,
      hashedEmail,
      hashedExternalId,
    });
  }

  if (isGA4Initialized) {
    ReactGA.event('start_trial', {
      value: 0,
      currency: 'EUR',
    });
  }

  // Hotjar Event
  trackHotjarEvent('trial_started');
};

/**
 * Track subscription cancellation
 * @param {string} plan - Plan name that was cancelled
 */
export const trackSubscriptionCancellation = (plan) => {
  trackEvent('CancelSubscription', {
    content_name: `${plan} Plan`,
    content_category: 'subscription',
  });
};

/**
 * Track feature usage
 * @param {string} feature - Feature name
 */
export const trackFeatureUsage = (feature) => {
  trackEvent('CustomizeProduct', {
    content_type: 'feature',
    content_name: feature,
  });
};

/**
 * Track quota exceeded
 * @param {string} quotaType - Type of quota exceeded
 */
export const trackQuotaExceeded = (quotaType) => {
  trackEvent('FindLocation', { // Using FindLocation for quota limit
    content_name: `Quota Exceeded: ${quotaType}`,
    content_category: 'limitation',
  });

  // Hotjar Event - valuable for finding upgrade potential
  trackHotjarEvent('quota_exceeded');
};

/**
 * Track contact/support request
 */
export const trackContactSupport = () => {
  trackEvent('Contact', {
    content_name: 'Support Request',
  });
};

// ============================================
// CAPI (Conversions API) Support Functions
// ============================================

/**
 * Generate unique event ID for CAPI deduplication.
 * Format: {userId}_{eventName}_{timestamp}_{uuid}
 *
 * @param {string} userId - User's internal ID
 * @param {string} eventName - Event name (Purchase, CompleteRegistration, etc.)
 * @returns {string} Unique event ID
 */
export const generateEventId = (userId, eventName) => {
  const timestamp = Date.now();
  const uuid = crypto.randomUUID();
  return `${userId}_${eventName}_${timestamp}_${uuid}`;
};

/**
 * Get cookie value by name.
 *
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null
 */
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
};

/**
 * Get URL parameter value.
 *
 * @param {string} name - Parameter name
 * @returns {string|null} Parameter value or null
 */
const getUrlParam = (name) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
};

/**
 * Collect CAPI metadata for user registration.
 * This data is stored in Supabase user_metadata and used by
 * the backend for all CAPI events (Registration, Purchase, Subscribe, etc.)
 *
 * @returns {Object} CAPI metadata object for Supabase user_metadata
 */
export const collectCAPIRegistrationMetadata = () => {
  const fbp = getCookie('_fbp');
  const fbc = getCookie('_fbc');
  const fbclid = getUrlParam('fbclid');

  return {
    capi_fbp: fbp || null,
    capi_fbc: fbc || null,
    capi_fbclid: fbclid || null,
    capi_registration_url: window.location.href,
    capi_registration_ua: navigator.userAgent,
    capi_registered_at: new Date().toISOString(),
  };
};

/**
 * Collect CAPI browser context for login (fills missing values only).
 * Used to supplement user_metadata if fbp/fbc weren't available at registration.
 *
 * @returns {Object} Browser context with only non-null values
 */
export const collectCAPIBrowserContext = () => {
  const context = {};

  const fbp = getCookie('_fbp');
  const fbc = getCookie('_fbc');
  const fbclid = getUrlParam('fbclid');

  if (fbp) context.capi_fbp = fbp;
  if (fbc) context.capi_fbc = fbc;
  if (fbclid) context.capi_fbclid = fbclid;

  return context;
};

/**
 * Collect browser context for CAPI.
 * Includes Facebook browser cookies and user agent.
 *
 * @returns {Object} Browser context object
 */
export const collectBrowserContext = () => ({
  fbp: getCookie('_fbp'),
  fbc: getCookie('_fbc') || (getUrlParam('fbclid') ? `fb.1.${Date.now()}.${getUrlParam('fbclid')}` : null),
  event_source_url: window.location.href,
  client_user_agent: navigator.userAgent,
});

/**
 * Send browser context to backend for CAPI deduplication.
 *
 * @param {Object} data - Context data
 * @param {string} data.event_id - Event ID (same as Pixel event_id)
 * @param {string} data.event_name - Event name
 * @param {string} data.fbp - _fbp cookie
 * @param {string} data.fbc - _fbc cookie or fbclid
 * @param {string} data.event_source_url - Page URL
 * @param {string} data.client_user_agent - Browser user agent
 * @returns {Promise<Response|null>}
 */
export const sendCAPIContext = async (data) => {
  try {
    // Get auth token from localStorage (Supabase stores it there)
    const authData = localStorage.getItem('sb-auth-token');
    let token = null;
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        token = parsed?.access_token;
      } catch {
        // Try direct token storage
        token = authData;
      }
    }

    if (!token) {
      console.warn('[Analytics] CAPI context not sent - no auth token');
      return null;
    }

    const response = await fetch('/api/capi/context', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.warn('[Analytics] Failed to send CAPI context:', response.status);
    }

    return response;
  } catch (error) {
    // Non-critical: log but don't throw
    console.warn('[Analytics] CAPI context send error:', error);
    return null;
  }
};

/**
 * Send CompleteRegistration event via CAPI
 * Call this after successful user registration to track via both Pixel and CAPI
 *
 * @param {Object} data
 * @param {string} data.event_id - Event ID for deduplication
 * @param {string} data.fbp - _fbp cookie value
 * @param {string} data.fbc - _fbc cookie or fbclid
 * @param {string} data.event_source_url - Page URL
 * @param {string} data.client_user_agent - Browser user agent
 * @returns {Promise<Object|null>} - CAPI response or null on error
 */
export const sendCAPIRegistration = async (data, authToken = null) => {
  try {
    // Get auth token from localStorage (Supabase stores it there)
    let token = authToken;
    if (!token) {
      const authData = localStorage.getItem('sb-auth-token');
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          token = parsed?.access_token;
        } catch {
          // Try direct token storage
          token = authData;
        }
      }
    }

    if (!token) {
      console.warn('[Analytics] CAPI registration not sent - no auth token');
      return null;
    }

    const response = await fetch('/api/capi/registration', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.warn('[Analytics] Failed to send CAPI registration:', response.status);
      return null;
    }

    const result = await response.json();
    console.log('[Analytics] CAPI CompleteRegistration tracked:', result);
    return result;
  } catch (error) {
    // Non-critical: log but don't throw
    console.warn('[Analytics] CAPI registration send error:', error);
    return null;
  }
};

// Export for advanced usage
export { ReactPixel };
